import React from 'react';
import { styled } from '@compiled/react';
import AkColorPicker from '@atlaskit/color-picker';
import type { PopupSelectProps } from '@atlaskit/select';

import { token } from '@atlaskit/tokens';

import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import {
	MAP_COLORS_TO_VALUE,
	MAP_VALUE_TO_COLOR,
} from '@atlassian/jira-issue-epic-color/src/common/constants.tsx';
import { colorFieldDataTestId, getI18nColorPalette } from '../../common/utils.tsx';
import messages from './messages.tsx';
import type { Props } from './types.tsx';

const popperProps: PopupSelectProps['popperProps'] = {
	strategy: 'fixed',
	modifiers: [
		{
			name: 'offset',
			options: {
				offset: [-10, 5],
			},
		},
	],
};
export const ColorPicker = ({ value: color, showColorName, onChange }: Props) => {
	const intl = useIntl();
	const colorPalette = getI18nColorPalette(intl);

	const colorString = MAP_COLORS_TO_VALUE[color];

	const maybeColorLabel = colorPalette.filter((pcol) => pcol.value === colorString)[0];
	const colorLabel = (maybeColorLabel && maybeColorLabel.label) || '';

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const onChangeHandler = (changedTo: any) => {
		// changedTo is a CSS value string like '#FF7452', so we have to map it back to Color
		onChange(MAP_VALUE_TO_COLOR[changedTo]);
	};

	return (
		<ColorPickerWrapper data-testid={colorFieldDataTestId}>
			<AkColorPicker
				label={intl.formatMessage(messages.label)}
				palette={colorPalette}
				selectedColor={colorString}
				cols={colorPalette.length / 2}
				onChange={onChangeHandler}
				popperProps={popperProps}
			/>
			{showColorName && <ColorName>{colorLabel}</ColorName>}
		</ColorPickerWrapper>
	);
};

export default ColorPicker;

// Move 2px to the left to align with other components
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ColorPickerWrapper = styled.div({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	marginLeft: token('space.negative.025'),
	paddingTop: token('space.100'),
	paddingRight: token('space.100'),
	height: '32px',
	// color picker focused style clashes with some global css style on the page
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'button:focus': {
		boxShadow: 'none',
	},
});
ColorPickerWrapper.displayName = 'ColorPickerWrapper';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ColorName = styled.p({
	marginTop: 0,
	marginRight: 0,
	marginBottom: 0,
	marginLeft: token('space.050'),
});
ColorName.displayName = 'ColorName';
